import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import { StyledLabel, StyledMandatory } from '@components/Datepicker/DatepickerTsoPq';
import { faCheck, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, styled, Typography, TypographyProps } from '@mui/material';
import { ApiExternalDtoTsoExternalDto } from '@redux/apiSlices/tsoApi';
import { useEffect, useState } from 'react';

type TsosStateProps = {
  id: number;
  name: string;
  active: boolean;
  abbreviation: string;
};

type ChipSelectorTsoPqProps = {
  chips: ChipTsoPqProp[];
  customWidth?: string;
  mandatory?: boolean;
  supportingText?: string;
  error?: boolean;
  label?: string;
  activeChips: { id: number; name: string; active: boolean }[];
  handleChange?: (chipsState: TsosStateProps[]) => void;
  name?: string;
  setValue?: any;
  disabled?: boolean;
  multiple?: boolean;
  info?: string;
};

export type ChipTsoPqProp = ApiExternalDtoTsoExternalDto & {
  active: boolean;
};

type StyledSupportingTextProps = TypographyProps & {
  error?: boolean;
};

export const colorMap = {
  Amprion: {
    active: tsoPqTheme.palette.background.tso.active.color_tso_background_amprion,
    hover: tsoPqTheme.palette.background.tso.hover.color_tso_background_amprion,
    pressed: tsoPqTheme.palette.background.tso.pressed.color_tso_background_amprion,
  },
  Tennet: {
    active: tsoPqTheme.palette.background.tso.active.color_tso_background_tennet,
    hover: tsoPqTheme.palette.background.tso.hover.color_tso_background_tennet,
    pressed: tsoPqTheme.palette.background.tso.pressed.color_tso_background_tennet,
  },
  '50Hertz': {
    active: tsoPqTheme.palette.background.tso.active.color_tso_background_50hertz,
    hover: tsoPqTheme.palette.background.tso.hover.color_tso_background_50hertz,
    pressed: tsoPqTheme.palette.background.tso.pressed.color_tso_background_50hertz,
  },

  TransnetBW: {
    active: tsoPqTheme.palette.background.tso.active.color_tso_background_transnet,
    hover: tsoPqTheme.palette.background.tso.hover.color_tso_background_transnet,
    pressed: tsoPqTheme.palette.background.tso.pressed.color_tso_background_transnet,
  },
  default: {
    active: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent,
    hover: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent,
    pressed: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent,
  },
  defaultInactive: {
    active: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent,
    hover: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent,
    pressed: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent,
  },
};

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => !['customWidth', 'error', 'disabled'].includes(prop as string),
})(({ customWidth, error, disabled }: { customWidth?: string; error: boolean; disabled: boolean }) => {
  return {
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    userSelect: disabled ? 'none' : 'auto',
    width: customWidth ?? '31.5rem',
    height: '1.625rem',
    display: 'flex',
    alignItems: 'flex-start',
    padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8}`,
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    border: '1px solid',
    borderColor: error ? tsoPqTheme.palette.customError.color_signal_error_error : '#0000003b', //todo change color to design color after changing all other fields colors
    background: tsoPqTheme.palette.background.other.color_other_background_white,
  };
});

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => !['active'].includes(prop as string),
})(({ active, label }: { active: boolean; label: string }) => {
  const chipColor = Object.keys(colorMap).find((colorLabel) => colorLabel === label) ?? 'default';

  return {
    height: '1.625rem',
    display: 'flex',
    padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8}`,
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    background: active
      ? colorMap[chipColor as keyof typeof colorMap].active
      : tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl,
    color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
    fontSize: '0.75rem',
    lineHeight: '1.125rem',

    '&:hover': {
      cursor: 'pointer',
      background: active
        ? colorMap[chipColor as keyof typeof colorMap].hover
        : tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent,
    },

    '&:active': {
      boxShadow: 'none',
      background: active
        ? colorMap[chipColor as keyof typeof colorMap].pressed
        : tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_dark,
    },

    '& .MuiTouchRipple-root': {
      display: 'none',
    },

    '& .MuiChip-label': {
      padding: 0,
    },

    '& .MuiChip-icon': {
      margin: 0,
      width: '0.75rem',
    },
  };
});

const StyledContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    width: '100%',
  };
});

const StyledInfoIcon = styled(IconButtonTsoPq)(() => {
  return {
    margin: `0 ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4}`,
    alignSelf: 'center',

    '&:hover': {
      cursor: 'pointer',
    },
  };
});

const StyledSupportingText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'error',
})<StyledSupportingTextProps>(({ error }) => {
  return {
    color: error
      ? tsoPqTheme.palette.customError.color_signal_error_error
      : tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '0.875rem',
    padding: '0 0 0 0.5rem',
  };
});

const ChipSelectorTsoPq = ({
  chips = [],
  customWidth,
  label,
  mandatory = false,
  supportingText,
  error = false,
  handleChange,
  activeChips = [],
  name,
  setValue,
  disabled = false,
  multiple = true,
  info = undefined,
}: ChipSelectorTsoPqProps) => {
  const [chipsData, setChipsData] = useState<ChipTsoPqProp[]>([]);

  const sortChips = (chips: ChipTsoPqProp[]) => {
    return chips.toSorted((chipOne: ChipTsoPqProp, chipTwo: ChipTsoPqProp) => chipOne.name.localeCompare(chipTwo.name));
  };

  const defaultChips = sortChips(chips);

  useEffect(() => {
    const updateChecked = defaultChips.map((chip) => ({
      ...chip,
      active: activeChips.some((active) => active.id === chip.id),
    }));
    setChipsData(updateChecked);
  }, [chips]);

  useEffect(() => {
    if (activeChips.length === 0) {
      setChipsData(defaultChips);
    }

    if (setValue) {
      setValue(name, activeChips);
    }

    if (activeChips.length > 0) {
      setChipsData(
        defaultChips.map((chip) => ({ ...chip, active: activeChips.some((activeChip) => activeChip.id === chip.id) }))
      );
    }
  }, [activeChips, name, setValue]);

  const handleActive = (targetlabel: string) => {
    if (multiple)
      setChipsData((prev) => {
        const updated = prev.map((chip) => (chip.name === targetlabel ? { ...chip, active: !chip.active } : chip));
        if (handleChange) {
          const activeChips = updated
            .map((chip) => {
              return { id: chip.id, name: chip.name, active: chip.active, abbreviation: chip.abbreviation };
            })
            .filter((chip) => chip.active) as TsosStateProps[];

          handleChange(activeChips);
        }

        return updated;
      });
    if (!multiple) {
      const selectedChip = defaultChips.map((chip) => (chip.name === targetlabel ? { ...chip, active: true } : chip));
      setChipsData(selectedChip);
      handleChange?.([
        {
          id: selectedChip.filter((chip) => chip.active === true)[0].id ?? NaN,
          name: selectedChip.filter((chip) => chip.active === true)[0].name ?? '',
          active: true,
          abbreviation: selectedChip.filter((chip) => chip.active === true)[0].abbreviation ?? '',
        },
      ]);
    }
  };

  return (
    <StyledContainer>
      <StyledLabel>
        {label}
        {mandatory && <StyledMandatory>*</StyledMandatory>}
        {info && (
          <StyledInfoIcon icon={faInfoCircle} buttonType="transparentNeutralNoEffect" sizeVariant="xs" tooltip={info} />
        )}
      </StyledLabel>
      <StyledBox customWidth={customWidth} error={error} disabled={disabled}>
        {chipsData.map((chip: ChipTsoPqProp) => (
          <StyledChip
            key={chip.id}
            icon={chip.active ? <FontAwesomeIcon icon={faCheck} /> : undefined}
            label={chip.name}
            active={chip.active}
            onClick={() => !disabled && handleActive(chip.name)}
          />
        ))}
      </StyledBox>
      {supportingText && <StyledSupportingText error={error}>{supportingText}</StyledSupportingText>}
    </StyledContainer>
  );
};

export default ChipSelectorTsoPq;
